import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { getPasswordManagerRefreshToken, setPasswordManagerAccessToken } from "@src/utils/passwordManagerAuth";

interface IRefreshPasswordManagerTokenState {
	loading: boolean;
	error: string | null;
}

const initialState: IRefreshPasswordManagerTokenState = {
	loading: false,
	error: null,
};

const buildUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Authentication/refresh-token`;
};

export const refreshPasswordManagerToken = createAsyncThunk<string>(
	"passwordManager/refreshPasswordManagerToken",
	async (_, { rejectWithValue }) => {
		try {
			const refreshToken = getPasswordManagerRefreshToken();
			if (!refreshToken) return rejectWithValue("Missing refresh token");

			const response = await axios.post<{ accessToken?: { result?: string } }>(buildUrl(), {
				refreshToken,
			});

			const accessToken = response.data?.accessToken?.result;
			if (!accessToken) return rejectWithValue("Invalid refresh token response");

			setPasswordManagerAccessToken(accessToken);
			return accessToken;
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to refresh token";
			return rejectWithValue(message);
		}
	},
);

const refreshPasswordManagerTokenSlice = createSlice({
	name: "passwordManager/refreshPasswordManagerToken",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(refreshPasswordManagerToken.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(refreshPasswordManagerToken.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(refreshPasswordManagerToken.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to refresh token";
			});
	},
});

export const refreshPasswordManagerTokenReducer = refreshPasswordManagerTokenSlice.reducer;
